import React from 'react';
import { FileTextIcon } from 'lucide-react';
import type {
  CoInvestigatorRow,
  DocumentRow,
  GanttRow,
  ProposalBodyRow,
  SubmissionRow
} from '../api/types';
import { documentUrl } from '../api/research';
import { formatDateTime, ganttMonthCount, monthsFromRow } from '../data/fieldMap';

/**
 * The whole proposal as a committee member or reviewer reads it — the same
 * sections the researcher filled in, in the same order, without any controls.
 */

interface ProposalReadViewProps {
  body: ProposalBodyRow | null;
  typeNames: string[];
  gantt: GanttRow[];
  submission: SubmissionRow | null;
  coInvestigators: CoInvestigatorRow[];
  documents: DocumentRow[];
  sessionToken: string;
  researchCode: number;
}

function Prose({ value }: { value?: string | null }) {
  const text = (value ?? '').trim();
  if (!text) return <p className="muted">Not provided</p>;
  return <p className="review-prose">{text}</p>;
}

function Block({ title, value }: { title: string; value?: string | null }) {
  return (
    <div className="review-block">
      <h4 className="review-block-title">{title}</h4>
      <Prose value={value} />
    </div>
  );
}

function Line({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="review-line">
      <span className="review-line-key">{label}</span>
      <span className="review-line-value">
        {value || <em className="muted">Not provided</em>}
      </span>
    </div>
  );
}

export function ProposalReadView({
  body,
  typeNames,
  gantt,
  submission,
  coInvestigators,
  documents,
  sessionToken,
  researchCode
}: ProposalReadViewProps) {
  const monthCount = ganttMonthCount(body?.DurationMonths ?? '');
  const months = Array.from({ length: monthCount }, (_, i) => i + 1);

  return (
    <div className="stack">
      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Proposal identification</h2>
        </div>
        <div className="card-body">
          <Line label="Title" value={body?.Title} />
          <Line label="Review category" value={body?.ReviewCategory} />
          <Line label="Proposed start" value={body?.StartDate} />
          <Line
            label="Duration"
            value={body?.DurationMonths ? `${body.DurationMonths} months` : ''}
          />
          <div className="review-block">
            <h4 className="review-block-title">Type of research project</h4>
            {typeNames.length > 0 ? (
              <ul className="review-tags">
                {typeNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            ) : (
              <p className="muted">None selected</p>
            )}
            {body?.HasOtherType && body.OtherTypeReason ? (
              <Prose value={body.OtherTypeReason} />
            ) : null}
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Research proposal</h2>
        </div>
        <div className="card-body">
          <Block title="Introduction" value={body?.Introduction} />
          <Block title="Rationale" value={body?.Rationale} />
          <Block title="Objectives" value={body?.Objectives} />
          <Block title="Study type, setting and duration" value={body?.StudyType} />
          <Block title="Sample size / statistical power" value={body?.SampleSize} />
          <Block title="Sampling technique" value={body?.SamplingTech} />
          <Block title="Study population" value={body?.StudyPopulation} />
          <Block title="Inclusion criteria" value={body?.Inclusion} />
          <Block title="Exclusion criteria" value={body?.ExclCriteria} />
          <Block title="Study instrument" value={body?.StudyInstrument} />
          <Block title="Procedure / methods" value={body?.ProcedureDet} />
          <Block title="Data analysis procedure" value={body?.DataAnalysis} />
          <Block title="References" value={body?.ReferencesDet} />
          <Block title="Institutional facilities" value={body?.InstitutionalFacilities} />
          <Block title="Potential benefits" value={body?.PotentialBenefits} />
          <Block title="Potential risks" value={body?.PotentialRisks} />
          <Block title="Ethical considerations" value={body?.Ethical} />
          <Block title="Dissemination of results" value={body?.Dissemination} />
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Month-wise time frame</h2>
        </div>
        <div className="card-body">
          {gantt.length === 0 ? (
            <p className="muted">No activities recorded.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col" style={{ minWidth: 220 }}>
                      Activity
                    </th>
                    {months.map((m) => (
                      <th scope="col" key={m} className="cell-center">
                        M{m}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gantt.map((row, index) => {
                    const flags = monthsFromRow(row as unknown as Record<string, unknown>, monthCount);
                    return (
                      <tr key={row.ActivityID ?? index}>
                        <td>{String(row.Activity)}</td>
                        {flags.map((on, i) => (
                          <td key={i} className="cell-center">
                            <span className={`gantt-cell${on ? ' is-on' : ''}`} aria-hidden="true">
                              {on ? 'X' : ''}
                            </span>
                            <span className="sr-only">
                              {on ? `Active in month ${i + 1}` : `Not active in month ${i + 1}`}
                            </span>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Submission details</h2>
        </div>
        <div className="card-body">
          <Line
            label="Grant required from SMDC"
            value={
              submission
                ? submission.GrantRequired
                  ? `Yes — PKR ${submission.GrantAmount.toLocaleString()}`
                  : 'No'
                : ''
            }
          />
          <Line label="Principal Investigator" value={submission?.PiName} />
          <Line label="Title / position" value={submission?.PiTitle} />
          <Line label="Institution" value={submission?.PiInstitution} />
          <Line label="Contact no." value={submission?.PiContact} />
          <Line label="Email" value={submission?.PiEmail} />
          <Line label="Head of Department" value={submission?.HodName} />
          <Line label="Supervisor" value={submission?.SupervisorName} />

          <div className="review-block">
            <h4 className="review-block-title">Co-investigators</h4>
            {coInvestigators.length === 0 ? (
              <p className="muted">None listed</p>
            ) : (
              <ul className="review-tags">
                {coInvestigators.map((ci) => (
                  <li key={ci.CoInvestigatorID}>
                    {ci.Name} — {ci.Title}
                    {ci.Department ? `, ${ci.Department}` : ''}
                    {ci.Contact ? ` · ${ci.Contact}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Associated documents</h2>
        </div>
        <div className="card-body">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col" style={{ minWidth: 320 }}>
                    Document
                  </th>
                  <th scope="col">File</th>
                  <th scope="col">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.DocCode}>
                    <td>{doc.DocName}</td>
                    <td>
                      {doc.IsUploaded ? (
                        <a
                          className="file-name"
                          href={documentUrl(sessionToken, researchCode, doc.DocCode)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FileTextIcon size={14} aria-hidden="true" />
                          Open PDF
                        </a>
                      ) : (
                        <span className="muted">Not uploaded</span>
                      )}
                    </td>
                    <td className="muted">{formatDateTime(doc.LastUploadedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
